import CardType from './CardType';
import CardParameter from './CardParameter';
import CardDetailInformation from './CardDetailInformation';
import AdditionalInformation from './AdditionalInformation';
import { Button, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import { confirmDialog, successDialog, Text, warningDialog, useText } from '@src/components';
import {
  cardOrdersDelete,
  cardOrdersUpdate,
  getCurrentCardOrder,
  setCurrentCardOrder,
} from '@src/store/slice/cardOrderSlice';
import {
  cardOrderDetailsGelAll,
  resetDetail,
  cardOrderDetailsGetAllGsms,
} from '@src/store/slice/cardOrderDetailtsSlice';
import '@src/styles/cardOrder/cardInformation.scss';
import { useHistory } from 'react-router-dom';
import { getMaximumLoadedAmount } from '@src/store/slice/orderLimitsSlice';
import dayjs from '@src/utils/day';
import { dataLayer } from '@src/utils/utils';
import { VouIdCode } from '@src/store/enums';

const CardInformation = () => {
  const { currentCardOrder, cardOrderPopupDigital } = useSelector((state) => state?.cardOrder);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const history = useHistory();
  const [runResetCard, setRunResetCard] = useState(false);
  const { cardList, selectedExistUsersRow } = useSelector((state) => state?.cardOrderDetails);

  const [isExit, setIsExit] = useState(false);

  const giveUpText = useText('giveUp')
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

  useEffect(() => {
    dispatch(getMaximumLoadedAmount({ vouId: VouIdCode.Market }));
    dispatch(setCurrentCardOrder({ segId: 1100 }));
    if ([0, 1].includes(currentCardOrder.cardType)) {
      (async () => {
        await dispatch(cardOrderDetailsGelAll({ pageNumber: 1, pageSize: 10 }));
      })();
    }
  }, [currentCardOrder?.cardType, dispatch]);

  const warningHtmlContent = <Text t="youAreLeavingThePageTheOrderInformation" />;

  useEffect(() => {
    const historyBlock = history.block((location) => {
      if (isExit) {
        return true;
      }
      if (currentCardOrder?.id) {
        confirmDialog({
          title: <Text t="attention" />,
          htmlContent: warningHtmlContent,
          okText: <Text t="yes" />,
          cancelText: <Text t={giveUpText} />,
          closeIcon: false,
          onCancel: async (e) => {
            setIsExit(false);
            return false;
          },
          onOk: async () => {
            setIsExit(true);
            await dispatch(cardOrdersDelete());
            history.push(location.pathname);
          },
        });
      } else {
        return true;
      }

      return false;
    });

    return historyBlock;
  }, [history, isExit, dispatch, currentCardOrder]);

  const clickOrderApproval = useCallback(async () => {
    if (
      selectedExistUsersRow?.length > 0 &&
      currentCardOrder?.totalCount < selectedExistUsersRow?.length
    ) {
      warningDialog({
        title: <Text t="attention" />,
        message: <Text t="incompleteTransaction" />,
      });
      return;
    }
    if (
      Object.keys(detailForm.getFieldsValue()).length > 0 &&
      Object.keys(detailForm.getFieldValue()).length > 0 &&
      cardList?.length < 2
    ) {
      warningDialog({
        title: <Text t="attention" />,
        message: <Text t="incompleteTransaction" />,
      });
    } else {
      try {
        const additionalInformationFormValues = await form.validateFields();
        const cardActivationDate = additionalInformationFormValues.cardActivationDate
          ? additionalInformationFormValues.cardActivationDate
            ?.hour(23)
            .minute(59)
            .millisecond(0)
            .second(59)
            .format('YYYY-MM-DDTHH:mm:ssZ')
          : dayjs().hour(23).minute(59).millisecond(0).second(59).format('YYYY-MM-DDTHH:mm:ssZ');

        const currentGsms = await dispatch(cardOrderDetailsGetAllGsms()).unwrap();

        const body = {
          ...currentCardOrder,
          ...additionalInformationFormValues,
          orderStatus: 10,
          cardActivationDate,
          gsms: currentGsms,
        };

        if (body.cardType === 1) {
          body.printCompanyName = false;
          body.printNameSurname = false;
        }

        if (currentCardOrder.cardType === 2) {
          body.cardType = 1;
        }

        const response = await dispatch(cardOrdersUpdate(body));

        if (response?.payload?.data?.errorMessage) {
          warningDialog({
            title: <Text t="attention" />,
            message: response.payload.data.errorMessage,
          });
          return;
        }
        dataLayer(
          'kart_siparis_girisi',
          body?.cardType === 0 ? 'Fiziksel Kart' : 'Dijital Kart',
          cardOrderPopupDigital ? 'Pop up - Dijital Kart Seçin' : '',
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [currentCardOrder, dispatch, form, detailForm, cardList, selectedExistUsersRow]);

  const handleOrderCancel = useCallback(async () => {
    const action = await dispatch(cardOrdersDelete());
    if (cardOrdersDelete.fulfilled.match(action)) {
      successDialog({
        title: <Text t="successfullySent" />,
        message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
      });
      await dispatch(resetDetail());
      window.scrollTo(0, 0);

      await dispatch(getCurrentCardOrder({ vouId: VouIdCode.Market }));
      await dispatch(setCurrentCardOrder({ cardType: 1 }));
    }
  }, [dispatch]);

  const resetCard = () => {
    setRunResetCard(true);
  };

  const reResetCard = () => {
    setRunResetCard(false);
  };

  return (
    <div className="card-order-content order-input-form-page">
      <CardType form={detailForm} runResetCard={runResetCard} reResetCard={reResetCard} />

      {currentCardOrder.cardType === 0 && <CardParameter />}

      {[0, 1, 2].includes(currentCardOrder.cardType) && (
        <>
          <CardDetailInformation form={detailForm} resetCard={resetCard} />
          <AdditionalInformation form={form} />
        </>
      )}

      <div className="step-btn-container">
        <Button
          id="cardOrders-cardInformation-main-cancel-btn"
          className="cancel-btn"
          disabled={currentCardOrder?.id === 0}
          onClick={() => {
            return confirmDialog({
              title: <Text t="attention" />,
              htmlContent: <Text t="areYouSureYouWantToCancelThe" />,
              okText: <Text t="yes" />,
              cancelText: <Text t="no" />,
              onOk: async () => {
                return handleOrderCancel();
              },
              onCancel: async () => { },
            });
          }}
        >
          <Text t="cancelOrder" />
        </Button>
        <Button
          id="cardOrders-cardInformation-main-next-btn"
          className="next-btn"
          onClick={clickOrderApproval}
          disabled={currentCardOrder?.totalCount === 0}
        >
          <Text t="approveOrder" />
        </Button>
      </div>
    </div>
  );
};

export default CardInformation;
